/** @jsxImportSource @emotion/react */
import React, { memo, useState, useCallback, Suspense } from 'react'
import colors from '@sportninja/common/constants/appColors'
import Text from '../ts-components/Text'
import { css } from '@emotion/react'
import { InfoCircleOutlined } from '@ant-design/icons'

const SchedulerVersionsModal = React.lazy(
  () => import('../SchedulerVersionsModal')
)

interface Props {
  gamesCount: number
  schedulerStatus?: string | null
  scheduleId?: string
  scheduleName?: string
}

const tagStyles = css`
  min-width: 100px;
  text-align: center;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 6px 8px;
  border-radius: 8px;
  gap: 8px;
  cursor: pointer;
  border: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`

interface StatusConfig {
  text: string
  backgroundColor: string
  borderColor?: string
}

export interface SchedulerStatusInfoItem {
  text: string
  color: string
  description: string
}

export const SCHEDULER_STATUS_INFO: SchedulerStatusInfoItem[] = [
  {
    text: 'Not Set',
    color: colors.NEUTRAL_100,
    description: 'No schedule has been created yet',
  },
  {
    text: 'Generated',
    color: colors.SUCCESS_300,
    description: 'Games created, ready for review',
  },
  {
    text: 'Partial',
    color: colors.WARNING_300,
    description: 'Some divisions succeeded, some failed',
  },
  {
    text: 'Failed',
    color: colors.ERROR_200,
    description: 'Schedule generation failed',
  },
  {
    text: 'Published',
    color: colors.SUCCESS_300,
    description: 'Games have been published',
  },
]

function getStatusConfig(
  schedulerStatus: string | null | undefined,
  gamesCount: number
): StatusConfig {
  switch (schedulerStatus) {
    case 'pending':
      return {
        text: 'Configuring',
        backgroundColor: colors.NEUTRAL_100,
        borderColor: colors.NEUTRAL_200,
      }
    case 'processing':
      return {
        text: 'Generating...',
        backgroundColor: `${colors.PRIMARY}33`,
      }
    case 'generated':
      return {
        text: 'Generated',
        backgroundColor: `${colors.SUCCESS_300}33`,
      }
    case 'partially_generated':
      return {
        text: 'Partial',
        backgroundColor: `${colors.WARNING_300}33`,
      }
    case 'failed':
      return {
        text: 'Failed',
        backgroundColor: `${colors.ERROR_200}33`,
      }
    case 'applied':
      return {
        text: 'Published',
        backgroundColor: `${colors.SUCCESS_300}33`,
      }
    case 'cancelled':
      return {
        text: 'Cancelled',
        backgroundColor: colors.NEUTRAL_100,
        borderColor: colors.NEUTRAL_200,
      }
    default:
      if (gamesCount === 0) {
        return {
          text: 'Not Set',
          backgroundColor: colors.NEUTRAL_100,
          borderColor: colors.NEUTRAL_200,
        }
      }
      return {
        text: 'Active',
        backgroundColor: `${colors.SUCCESS_300}33`,
      }
  }
}

const ScheduleStatusTag = ({
  gamesCount,
  schedulerStatus,
  scheduleId,
  scheduleName,
}: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (scheduleId) {
        setIsModalOpen(true)
      }
    },
    [scheduleId]
  )

  const handleClose = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const config = getStatusConfig(schedulerStatus, gamesCount)

  return (
    <>
      <button
        type='button'
        css={[
          tagStyles,
          css`
            background-color: ${config.backgroundColor};
            ${config.borderColor
              ? `border: 1px solid ${config.borderColor};`
              : ''}
          `,
        ]}
        onClick={handleClick}
      >
        {!schedulerStatus && gamesCount === 0 && <InfoCircleOutlined />}
        <Text
          variant='paragraphXSmall'
          weight='medium'
          color={colors.NEUTRAL_0}
        >
          {config.text}
        </Text>
      </button>
      {scheduleId && isModalOpen && (
        <Suspense fallback={null}>
          <SchedulerVersionsModal
            isOpen={isModalOpen}
            onClose={handleClose}
            scheduleId={scheduleId}
            scheduleName={scheduleName || ''}
          />
        </Suspense>
      )}
    </>
  )
}

export default memo(ScheduleStatusTag)
